import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {makeSizeConstants} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {computeIsGlobalNamespace, isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {TeamModel} from '../../../models/api/teamModel';
import {getCellTextColor} from '../table/helpers/tableCellHelpers';
import {TeamAvatar} from './teamAvatar';

/*
 * Props.
 */

interface NamespaceAvatarProps {
  namespace: string;
  isSelected?: boolean;
  iconSize?: VisualSizesEnum;
  team?: TeamModel;
  shouldRenderLabel?: boolean;
}

/*
 * Styles.
 */

interface StyledLabelDivProps {
  $isSelected: boolean;
}

const StyledLabelDiv = styled.div<StyledLabelDivProps>`
  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
  margin-left: 8px;
`;

/*
 * Component.
 */

export const NamespaceAvatar: FC<NamespaceAvatarProps> = ({
  namespace,
  team,
  isSelected = false,
  iconSize = VisualSizesEnum.SMALL,
  shouldRenderLabel = false,
}) => {
  const theme = useUserTheme();
  const iconSizes = makeSizeConstants(16, 20, 24);
  const size = iconSizes[iconSize];
  if (team) {
    return (
      <>
        <TeamAvatar team={team} size={iconSize} />
        {shouldRenderLabel && <StyledLabelDiv $isSelected={Boolean(isSelected)}>{team.name}</StyledLabelDiv>}
      </>
    );
  }
  if (computeIsGlobalNamespace(namespace)) {
    return (
      <>
        <Icon name="buildingFilledSmall" size={size} color={theme.palette.teal.shade40} />
        {shouldRenderLabel && (
          <StyledLabelDiv $isSelected={Boolean(isSelected)}>
            <Trans id="namespace-avatar-company">Company</Trans>
          </StyledLabelDiv>
        )}
      </>
    );
  }
  if (isTeammateNamespace(namespace)) {
    return (
      <>
        <Icon name="lockSmall" size={size} color={theme.palette.grey.shade40} />
        {shouldRenderLabel && (
          <StyledLabelDiv $isSelected={Boolean(isSelected)}>
            <Trans id="namespace-avatar-private">Private</Trans>
          </StyledLabelDiv>
        )}
      </>
    );
  }
  return null;
};
